import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { BrandingData } from '../../services/institutionApi';

export interface BrandingState {
  primaryColor: string;
  secondaryColor: string;
  logoUrl: string | null;
  faviconUrl: string | null;
  isLoaded: boolean;
}

// Default fallback colors (current hardcoded values)
const DEFAULT_PRIMARY_COLOR = '#631760';
const DEFAULT_SECONDARY_COLOR = '#AD14A7';

// Helper function to get branding from localStorage
const getBrandingFromStorage = (): Partial<BrandingState> => {
  try {
    const brandingStr = localStorage.getItem('institutionBranding');
    return brandingStr ? JSON.parse(brandingStr) : {};
  } catch (error) {
    console.error('Error parsing branding from localStorage:', error);
    return {};
  }
};

const storedBranding = getBrandingFromStorage();

const initialState: BrandingState = {
  primaryColor: storedBranding.primaryColor || DEFAULT_PRIMARY_COLOR,
  secondaryColor: storedBranding.secondaryColor || DEFAULT_SECONDARY_COLOR,
  logoUrl: storedBranding.logoUrl !== undefined ? storedBranding.logoUrl : null,
  faviconUrl: storedBranding.faviconUrl !== undefined ? storedBranding.faviconUrl : null,
  // We mark it loaded if we successfully pulled it from local storage
  isLoaded: !!storedBranding.primaryColor,
};

const brandingSlice = createSlice({
  name: 'branding',
  initialState,
  reducers: {
    setBranding: (state, action: PayloadAction<Partial<BrandingData>>) => {
      if (action.payload.primaryColor) {
        state.primaryColor = action.payload.primaryColor;
      }
      if (action.payload.secondaryColor) {
        state.secondaryColor = action.payload.secondaryColor;
      }
      if (action.payload.logoUrl !== undefined) {
        state.logoUrl = action.payload.logoUrl || null;
      }
      if (action.payload.faviconUrl !== undefined) {
        state.faviconUrl = action.payload.faviconUrl || null;
      }
      state.isLoaded = true;
      
      // Persist to local storage so it survives logouts / reloads
      try {
        localStorage.setItem('institutionBranding', JSON.stringify({
          primaryColor: state.primaryColor,
          secondaryColor: state.secondaryColor,
          logoUrl: state.logoUrl,
          faviconUrl: state.faviconUrl
        }));
      } catch (e) {
        console.error('Failed to save branding to storage', e);
      }
    },
    resetBranding: (state) => {
      state.primaryColor = DEFAULT_PRIMARY_COLOR;
      state.secondaryColor = DEFAULT_SECONDARY_COLOR;
      state.logoUrl = null;
      state.faviconUrl = null;
      state.isLoaded = false;
      localStorage.removeItem('institutionBranding');
    },
  },
});

export const { setBranding, resetBranding } = brandingSlice.actions;
export default brandingSlice.reducer;




